import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import CensusErrorFormatStep from './CensusErrorFormatStep/CensusErrorFormatStep';
import CensusUploadStep, { type CensusInstruction } from './CensusUploadStep/CensusUploadStep';
import { TRANSLATIONS_QUOTES } from '../../../translations/quotes';
import { isFileValid } from '../../../utils/utils';
import CustomButton from '../../Buttons/CustomButton/CustomButton';
import CustomModal from '../CustomModal/CustomModal';

import './ModalCensus.scss';

type CensusStep = 'upload' | 'error-format' | (string & {});

export interface ModalCensusProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  step: CensusStep;
  file?: File | null;
  onFileSelect?: (file: File) => void;
  onClear?: () => void;
  onUpload?: () => void;
  uploadDisabled?: boolean;
  onDownloadTemplate: () => Promise<void>;
  onRetryUpload?: () => void;
  acceptedFormats?: string[];
  maxSizeMB?: number;
  children?: React.ReactNode;
  renderFooter?: () => React.ReactNode;
  onToggleInstructions?: () => void;
  instructions?: CensusInstruction[];
  uploadTitle?: string;
}

export default function ModalCensus({
  open,
  onClose,
  title,
  step,
  file,
  onFileSelect,
  onClear,
  onUpload,
  uploadDisabled,
  onDownloadTemplate,
  onRetryUpload,
  acceptedFormats,
  maxSizeMB,
  children,
  renderFooter,
  onToggleInstructions,
  instructions,
  uploadTitle
}: Readonly<ModalCensusProps>) {
  const { t } = useTranslation();
  const [formatError, setFormatError] = useState(false);

  const isInternalStep = step === 'upload' || step === 'error-format';

  const displayStep = formatError && step === 'upload' ? 'error-format' : step;

  let internalTitle = title;
  if (displayStep === 'upload') {
    internalTitle = uploadTitle ?? t(TRANSLATIONS_QUOTES.create.censusModal.title);
  } else if (displayStep === 'error-format') {
    internalTitle = t(TRANSLATIONS_QUOTES.create.censusModal.errorFormatModalTitle);
  }

  const handleFileSelect = (file: File) => {
    const formats = acceptedFormats ?? ['.csv', '.xlsx', '.xls'];
    const maxSize = maxSizeMB ?? 10;
    if (!isFileValid(file, formats, maxSize)) {
      setFormatError(true);
    } else {
      setFormatError(false);
      onFileSelect?.(file);
    }
  };

  const handleClear = () => {
    setFormatError(false);
    onClear?.();
  };

  const handleRetryUpload = () => {
    setFormatError(false);
    onRetryUpload?.();
  };

  return (
    <CustomModal open={open} onClose={onClose} title={internalTitle ?? ''} customClass="modal-census" size="l">
      {displayStep === 'upload' && (
        <>
          <div className="modal-census__body">
            <CensusUploadStep
              file={file ?? null}
              onFileSelect={handleFileSelect}
              onClear={handleClear}
              onDownloadTemplate={onDownloadTemplate}
              acceptedFormats={acceptedFormats}
              onToggleInstructions={onToggleInstructions}
              instructions={instructions}
            />
          </div>
          <div className="modal-census__footer">
            <CustomButton variant="default" size="m" onClick={onClose}>
              {t(TRANSLATIONS_QUOTES.create.censusModal.cancel)}
            </CustomButton>
            <CustomButton variant="primary" size="m" disabled={uploadDisabled} onClick={onUpload}>
              {t(TRANSLATIONS_QUOTES.create.censusModal.upload)} →
            </CustomButton>
          </div>
        </>
      )}
      {displayStep === 'error-format' && (
        <>
          <div className="modal-census__body">
            <CensusErrorFormatStep onDownloadTemplate={onDownloadTemplate} />
          </div>
          <div className="modal-census__footer">
            <CustomButton variant="default" size="m" onClick={onClose}>
              {t(TRANSLATIONS_QUOTES.create.censusModal.cancel)}
            </CustomButton>
            <CustomButton variant="primary" size="m" onClick={handleRetryUpload}>
              {t(TRANSLATIONS_QUOTES.create.censusModal.uploadDifferentFile)}
            </CustomButton>
          </div>
        </>
      )}
      {!isInternalStep && (
        <>
          <div className="modal-census__body">{children}</div>
          {renderFooter && <div className="modal-census__footer">{renderFooter()}</div>}
        </>
      )}
    </CustomModal>
  );
}
